import {
	CalendarOutlined,
	ClockCircleOutlined,
	CloudUploadOutlined,
	EyeOutlined,
	FileDoneOutlined,
	PercentageOutlined,
	PieChartOutlined,
	SyncOutlined,
	TableOutlined,
	UnorderedListOutlined,
} from '@ant-design/icons';
import { TutorialCompletion, TutorialName, TutorialStep } from './types';

/**
 * The end-to-end payroll allocation walkthrough.
 *
 * Order follows the actual workflow: pick a pay period and load employee costs,
 * bring in time activity, then review the resulting allocation and its journal.
 * Steps 6-10 cross into `/time-activity` and `/cost-allocations`; the tour driver
 * handles the navigation.
 */
const payrollAllocationSteps: TutorialStep[] = [
	{
		id: 'pay-period',
		route: '/employee-costs',
		pageLabel: 'Employee Costs',
		target: '[data-tour-target="tutorial-pay-period"]',
		icon: <CalendarOutlined />,
		title: 'Pick a Pay Period',
		description:
			'Everything on this page is scoped to one pay period. Use the arrows to move between periods, or open the picker to create a new one.',
		aiPrompt:
			'How do pay periods work in CostAllocation Pro, and how do I create or switch the pay period I am working in?',
		placement: 'bottom',
	},
	{
		id: 'sync-employees',
		route: '/employee-costs',
		pageLabel: 'Employee Costs',
		target: '[data-tour-target="tutorial-sync-employees"]',
		icon: <SyncOutlined />,
		title: 'Sync Employees from QuickBooks',
		description:
			'Pulls the employee list from QuickBooks. Run this whenever someone is hired, terminated, or renamed in QBO so the cost table below stays complete.',
		aiPrompt:
			'What exactly does syncing employees from QuickBooks change in CostAllocation Pro, and when should I run it?',
		placement: 'bottom',
	},
	{
		id: 'import-employee-cost',
		route: '/employee-costs',
		pageLabel: 'Employee Costs',
		target: '[data-tour-target="tutorial-import-employee-cost"]',
		icon: <CloudUploadOutlined />,
		title: 'Import Employee Costs',
		description:
			'Upload the earnings report from your payroll processor. You map its column headers once to cost categories like Salary, Payroll Taxes, and Fringe Benefits, and CAP remembers the mapping for next time.',
		aiPrompt:
			'Walk me through importing an employee cost file from my payroll processor, including how header mapping works.',
		placement: 'bottom',
	},
	{
		id: 'employee-cost-table',
		route: '/employee-costs',
		pageLabel: 'Employee Costs',
		target: '.ant-table-wrapper.employee-cost',
		icon: <TableOutlined />,
		title: 'Review Employee Costs',
		description:
			'Every cost that will be allocated lives here, grouped by the GL accounts configured in Settings. Values are editable and save as you leave a cell.',
		aiPrompt:
			'What do the columns in the employee cost table mean, and how do I change which ones appear?',
		placement: 'top',
	},
	{
		id: 'fixed-percentage',
		route: '/employee-costs',
		pageLabel: 'Employee Costs',
		target: '[data-tour-target="tutorial-fixed-percentage"]',
		icon: <PercentageOutlined />,
		title: 'Set a Fixed % Allocation',
		description:
			'The % icon pre-assigns an employee’s cost across Customer/Class combinations by percentage. Use it for people whose split is known up front instead of being derived from logged time.',
		aiPrompt:
			'What is a fixed % allocation for an employee, and when should I use it instead of time-based allocation?',
		placement: 'left',
	},
	{
		id: 'add-time-logs',
		route: '/time-activity',
		pageLabel: 'Time Activity',
		target: '[data-tour-target="tutorial-add-time-logs"]',
		icon: <ClockCircleOutlined />,
		title: 'Bring in Time Activity',
		description:
			'Time logs drive the allocation for everyone without a fixed percentage. Sync them from QuickBooks, import a file, or add them manually here.',
		aiPrompt:
			'How do time logs feed into payroll allocation, and what are my options for getting them into CostAllocation Pro?',
		placement: 'bottom',
	},
	{
		id: 'time-logs-table',
		route: '/time-activity',
		pageLabel: 'Time Activity',
		target: '[data-tour-target="tutorial-time-logs-table"]',
		icon: <UnorderedListOutlined />,
		title: 'Check the Time Logs',
		description:
			'Each entry assigns hours to a Customer/Class combination for one employee. Split an entry when the same block of time belongs to more than one customer.',
		aiPrompt:
			'How do I review and split time logs so hours land on the right customer and class?',
		placement: 'top',
	},
	{
		id: 'cost-allocation-table',
		route: '/cost-allocations?tab=costAllocation',
		pageLabel: 'Cost Allocations',
		target: '[data-tour-target="tutorial-cost-allocation-table"]',
		icon: <PieChartOutlined />,
		title: 'Review the Allocation',
		description:
			'Costs are now split across Customer/Class rows using logged hours or the fixed percentages you set. Totals here should tie back to the employee cost table.',
		aiPrompt:
			'How is my payroll cost split across customers and classes, and how do I check that it ties back to the employee cost totals?',
		placement: 'top',
	},
	{
		id: 'preview-journal',
		route: '/cost-allocations?tab=costAllocation',
		pageLabel: 'Cost Allocations',
		target: '[data-tour-target="tutorial-preview-journal"]',
		icon: <EyeOutlined />,
		title: 'Preview the Journal',
		description:
			'Preview the balanced debit/credit entry the allocation produces before anything is written to QuickBooks.',
		aiPrompt:
			'What does the journal preview show me before I publish, and what should I be checking on it?',
		placement: 'bottom',
	},
	{
		id: 'journal-entries',
		route: '/cost-allocations?tab=journalEntries',
		pageLabel: 'Cost Allocations',
		target: '[data-tour-target="tutorial-journal-entries"]',
		icon: <FileDoneOutlined />,
		title: 'Publish to QuickBooks',
		description:
			'The Journal Entries tab is the final step — review the entry one last time and publish it to QuickBooks Online.',
		aiPrompt:
			'How do I publish the payroll journal entry to QuickBooks, and what happens if I need to change it after publishing?',
		placement: 'top',
	},
];

export const TUTORIALS: Record<TutorialName, TutorialStep[]> = {
	payrollAllocation: payrollAllocationSteps,
};

export const TUTORIAL_COMPLETION: Record<TutorialName, TutorialCompletion> = {
	payrollAllocation: {
		title: 'You’re all set',
		description:
			'That’s the whole payroll allocation flow — from the pay period you’re costing to the journal entry that lands in QuickBooks.',
	},
};
